import './styles/EventCard.scss';

import { MoreVert } from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardHeader,
  CardProps,
  Divider,
  Grid,
  IconButton,
  Menu,
  Tooltip,
  Typography
} from '@mui/material';
import { ResolvedEvent } from '@thro-types/events';

import { isSameDay, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { ConditionalLink, DatePeriodChip } from 'app/components/';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { translations } from 'locales/i18n';
import { formatToDateAndTime } from 'utils/dates';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { InfoDialog } from '../components/InfoDialog';
import { OrganizerMenuActions } from '../components/actions';
import { getTemplateEvent, isEventActive, isOrganizer } from '../helper';
import { PercentageSpinner } from './components/PercentageSpinner';

interface CustomCardProps extends CardProps {
  content: string;
}

export const CustomCard = (props: CustomCardProps) => {
  return <Card {...props}>{props.children}</Card>;
};

interface Props {
  event: ResolvedEvent;
  isPreview: boolean;
  setEventRef?: (event: ResolvedEvent) => void;
  setFormModalOpen?: (state: boolean) => void;
  setDeleteModalOpen?: (state: boolean) => void;
  setCancelModalOpen?: (state: boolean) => void;
}

export const EventCard = ({
  event,
  isPreview,
  setEventRef,
  setFormModalOpen,
  setDeleteModalOpen,
  setCancelModalOpen
}: Props) => {
  const { t } = useTranslation();
  const { user: currentUser, authorRoles } = useAuthContext();
  const [canEdit, setCanEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [createInfoDialogOpen, setCreateInfoDialogOpen] = useState(false);
  const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };
  const handleSettingsMenuClose = (event: React.MouseEvent) => {
    setAnchorEl(null);
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (event.organizers) {
      setCanEdit(isOrganizer(currentUser, authorRoles, event.organizers, true, event.hostId));
    }
  }, [authorRoles, currentUser, event.hostId, event.organizers]);

  const isCancelled = event.isCancelled;
  const hasEnded = event.endDate?.toString() < new Date().toISOString();

  const confirmCreateInfo = () => {
    setEventRef && setEventRef(getTemplateEvent(event));
    setFormModalOpen && setFormModalOpen(true);
    setCreateInfoDialogOpen(false);
  };

  return (
    <>
      <InfoDialog
        dialogOpen={createInfoDialogOpen}
        handleConfirm={confirmCreateInfo}
        handleClose={() => setCreateInfoDialogOpen(false)}
      />
      <ConditionalLink to={`/events/${event.id}`} condition={!isPreview}>
        <CustomCard
          variant="elevation"
          content={
            isCancelled
              ? t(translations.pages.events.cancelled()).toUpperCase()
              : hasEnded
              ? t(translations.pages.events.ended()).toUpperCase()
              : ''
          }
          className={`event-card ${
            isCancelled ? 'event-card-cancelled' : hasEnded ? 'event-card-ended' : ''
          }`}
          sx={isMobile ? { marginX: '0.5em' } : undefined}
        >
          <CardHeader
            title={
              <Typography fontSize={'1.5rem'} style={{ wordBreak: 'break-word' }}>
                {event.name}
              </Typography>
            }
            action={
              canEdit &&
              !isMobile &&
              isEventActive(event) && (
                <>
                  <IconButton
                    aria-label="settings"
                    onClick={e => {
                      handleSettingsMenuClick(e);
                    }}
                    disabled={isPreview}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleSettingsMenuClose}
                    className="event-card__menu"
                  >
                    <OrganizerMenuActions
                      handleEditClick={e => {
                        handleSettingsMenuClose(e);
                        setEventRef && setEventRef(event);
                        setFormModalOpen && setFormModalOpen(true);
                      }}
                      handleCopyClick={e => {
                        handleSettingsMenuClose(e);
                        setCreateInfoDialogOpen && setCreateInfoDialogOpen(true);
                      }}
                      handleDeleteClick={e => {
                        handleSettingsMenuClose(e);
                        setEventRef && setEventRef(event);
                        setDeleteModalOpen && setDeleteModalOpen(true);
                      }}
                      handleCancelClick={e => {
                        handleSettingsMenuClose(e);
                        setEventRef && setEventRef(event);
                        setCancelModalOpen && setCancelModalOpen(true);
                      }}
                      hasEnrollments={event.occupancy > 0}
                      isActive={isEventActive(event)}
                    />
                  </Menu>
                </>
              )
            }
          />
          <Divider />
          <CardContent>
            <Grid container justifyContent={'space-between'}>
              <Grid item xs={6}>
                <LabeledInput label={t(translations.pages.events.create.host())}>
                  {event.host}
                </LabeledInput>
              </Grid>
              <Tooltip title={t(translations.pages.events.tooltips.occupancy())}>
                <Grid container item xs={2} justifyContent="center">
                  <PercentageSpinner occupancy={event.occupancy} />
                </Grid>
              </Tooltip>
              <Grid
                container
                justifyContent={'space-between'}
                width={'100%'}
                className="event-card__contentRow"
              >
                <Grid item xs={5}>
                  <LabeledInput label={t(translations.pages.events.create.regPeriodStart())}>
                    {formatToDateAndTime(event.registrationPeriodStart)}
                  </LabeledInput>
                </Grid>
                <Grid item xs={5}>
                  <LabeledInput label={t(translations.pages.events.create.regPeriodEnd())}>
                    {formatToDateAndTime(event.registrationPeriodEnd)}
                  </LabeledInput>
                </Grid>
              </Grid>
            </Grid>
            <Divider className="event-card__divider" />
            <Grid container className="event-card__contentRow">
              <Grid item>
                {!isSameDay(
                  parseISO(event.startDate?.toString()),
                  parseISO(event.endDate?.toString())
                ) ? (
                  <LabeledInput label={t(translations.pages.events.eventTimeSpan())}>
                    <DatePeriodChip
                      start={event.startDate}
                      end={event.endDate}
                      key={event.id}
                      type="STARTDATE-ENDDATE"
                    />
                  </LabeledInput>
                ) : (
                  <LabeledInput label={t(translations.pages.events.eventDate())}>
                    <DatePeriodChip
                      start={event.startDate}
                      end={event.endDate}
                      key={event.id}
                      type="DATE/STARTTIME-ENDTIME"
                    />
                  </LabeledInput>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </CustomCard>
      </ConditionalLink>
    </>
  );
};
